import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import api, { formatApiError } from "@/lib/api";

const PROJECT_TYPES = ["Security assessment", "Compliance readiness", "Managed security / retainer", "Baseline review ($499)", "Product inquiry (BArecord / SecureDesk)", "Partnership", "Other"];
const TIMELINES = ["As soon as possible", "Within 1–3 months", "3–6 months", "Just exploring"];

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", project_type: "", timeline: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent.");
      setForm({ name: "", email: "", company: "", project_type: "", timeline: "", message: "" });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-black py-24 lg:py-36 border-t border-white/10" data-testid="contact">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid gap-14 lg:grid-cols-[1fr_1fr]">
        <div>
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-fog mb-5">Start a conversation</p>
            <h2 className="font-display text-4xl sm:text-6xl tracking-tighter text-paper leading-[0.95] max-w-[12ch]">
              Let's talk about your risk.
            </h2>
            <p className="mt-8 text-sm text-fog leading-relaxed max-w-md">
              Tell us where you are today. We'll respond within one business day —
              or book a free 20-minute consult from your client portal
              (consults are typically scheduled within 12 weeks).
            </p>
            <div className="mt-8 space-y-4" data-testid="contact-phones">
              <div>
                <p className="text-xs uppercase tracking-widest text-fog">Sales &amp; general</p>
                <a href="tel:+14404639002" data-testid="contact-phone-1" className="block font-display text-2xl sm:text-3xl tracking-tight text-paper hover:text-fog transition-colors">
                  440-463-9002
                </a>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-fog">Support &amp; security incidents</p>
                <a href="tel:+18179939336" data-testid="contact-phone-2" className="block font-display text-2xl sm:text-3xl tracking-tight text-paper hover:text-fog transition-colors">
                  817-993-9336
                </a>
              </div>
            </div>
            <div className="mt-8 space-y-2" data-testid="contact-address">
              <p className="text-xs uppercase tracking-widest text-fog">Mailing address</p>
              <address className="not-italic text-sm text-paper/90 leading-relaxed">
                300 State Street, Unit 92532<br />
                Southlake, TX 76092
              </address>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          {sent ? (
            <div className="rounded-2xl border border-white/15 bg-surface p-10 text-center" data-testid="contact-success">
              <CheckCircle2 size={32} className="mx-auto text-green-400" />
              <h3 className="mt-5 font-display text-2xl tracking-tight text-paper">Message received.</h3>
              <p className="mt-3 text-sm text-fog leading-relaxed max-w-sm mx-auto">
                Thanks for reaching out — a confirmation is on its way to your inbox and
                our team will respond within one business day.
              </p>
              <button
                onClick={() => setSent(false)}
                data-testid="contact-send-another"
                className="mt-7 rounded-full border border-white/20 text-paper text-sm font-medium px-6 py-2.5 hover:bg-white/5 transition-colors"
              >
                Send another message
              </button>
            </div>
          ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="contact-form" noValidate={false}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name" value={form.name} onChange={set("name")} testid="contact-name" required />
              <Field label="Email" type="email" value={form.email} onChange={set("email")} testid="contact-email" required />
            </div>
            <Field label="Company" optional value={form.company} onChange={set("company")} testid="contact-company" />
            <div className="grid gap-4 sm:grid-cols-2">
              <SelectField label="Project type" optional value={form.project_type} onChange={set("project_type")} options={PROJECT_TYPES} testid="contact-project-type" />
              <SelectField label="Timeline" optional value={form.timeline} onChange={set("timeline")} options={TIMELINES} testid="contact-timeline" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-fog">
                Message <span aria-hidden="true" className="text-paper">*</span>
              </label>
              <textarea
                value={form.message}
                onChange={set("message")}
                required
                maxLength={4000}
                rows={4}
                data-testid="contact-message"
                className="mt-2 w-full bg-transparent border border-white/15 rounded-xl px-4 py-3 text-paper text-sm outline-none focus:border-white/50 transition-colors resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="group inline-flex items-center gap-2 rounded-full bg-paper text-black text-sm font-medium pl-6 pr-5 py-3.5 hover:bg-white transition-colors disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Send message"}
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <p className="text-xs text-fog/80 leading-relaxed max-w-md" data-testid="contact-consent-note">
              <span className="text-paper">*</span> Required. We use your details only to respond to your inquiry — never for
              unsolicited marketing. See our <Link to="/privacy" className="underline underline-offset-2 hover:text-paper transition-colors">Privacy Policy</Link>.
            </p>
          </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}

function SelectField({ label, value, onChange, options, testid, optional }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-fog">
        {label}
        {optional && <span className="text-fog/60 normal-case tracking-normal"> (optional)</span>}
      </label>
      <select
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="mt-2 w-full bg-black border border-white/15 rounded-xl px-4 py-3 text-sm outline-none focus:border-white/50 transition-colors appearance-none"
        style={{ color: value ? undefined : "#a1a1a6" }}
      >
        <option value="" className="bg-black text-fog">Select…</option>
        {options.map((o) => (
          <option key={o} value={o} className="bg-black text-paper">
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid, required, optional }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-fog">
        {label}
        {required && <span aria-hidden="true" className="text-paper"> *</span>}
        {optional && <span className="text-fog/60 normal-case tracking-normal"> (optional)</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="mt-2 w-full bg-transparent border border-white/15 rounded-xl px-4 py-3 text-paper text-sm outline-none focus:border-white/50 transition-colors"
      />
    </div>
  );
}
